import moment from "moment";

const normalizeStatus = (status) => String(status || "").toLowerCase().trim();

/**
 * Build analytics payload from log rows when /logs/analytics API is missing or empty.
 */
export function computeAnalyticsFromLogs(rows, period = "all") {
  const list = Array.isArray(rows) ? rows : [];
  const allTime = period === "all" || period === 0 || period === "0";
  const chartDays = allTime ? 30 : Math.min(90, Math.max(7, parseInt(period, 10) || 30));
  const chartSince = moment().subtract(chartDays - 1, "days").startOf("day");

  const counts = { synced: 0, failed: 0, skipped: 0, processing: 0 };
  let amountSynced = 0;
  let amountFailed = 0;

  const dailyMap = {};
  const amountMap = {};

  list.forEach((row) => {
    const created = row.created_at ? moment(row.created_at) : null;
    if (!allTime && (!created?.isValid() || created.isBefore(chartSince) || created.isAfter(moment()))) {
      return;
    }
    const status = normalizeStatus(row.status);
    if (counts[status] !== undefined) {
      counts[status] += 1;
    }

    const amount = parseFloat(row.amount) || 0;
    if (status === "synced") {
      amountSynced += amount;
    }
    if (status === "failed") {
      amountFailed += amount;
    }

    if (!created?.isValid()) {
      return;
    }

    if (created.isBefore(chartSince)) {
      return;
    }

    const day = created.format("YYYY-MM-DD");
    if (!dailyMap[day]) {
      dailyMap[day] = { synced: 0, failed: 0, skipped: 0, processing: 0 };
    }
    if (dailyMap[day][status] !== undefined) {
      dailyMap[day][status] += 1;
    }
    if (status === "synced") {
      amountMap[day] = (amountMap[day] || 0) + amount;
    }
  });

  const total = counts.synced + counts.failed + counts.skipped + counts.processing;
  const labels = [];
  const syncedSeries = [];
  const failedSeries = [];
  const skippedSeries = [];
  const amountSeries = [];

  for (let i = 0; i < chartDays; i++) {
    const day = chartSince.clone().add(i, "days").format("YYYY-MM-DD");
    labels.push(moment(day).format("D MMM"));
    const dayCounts = dailyMap[day] || {};
    syncedSeries.push(dayCounts.synced || 0);
    failedSeries.push(dayCounts.failed || 0);
    skippedSeries.push(dayCounts.skipped || 0);
    amountSeries.push(Math.round((amountMap[day] || 0) * 100) / 100);
  }

  return {
    days: allTime ? 0 : chartDays,
    all_time: allTime,
    source: "client_fallback",
    summary: {
      total,
      synced: counts.synced,
      failed: counts.failed,
      skipped: counts.skipped,
      processing: counts.processing,
      success_rate: total > 0 ? Math.round((counts.synced / total) * 1000) / 10 : 0,
      amount_synced: Math.round(amountSynced * 100) / 100,
      amount_failed: Math.round(amountFailed * 100) / 100,
    },
    by_status: [
      { status: "synced", count: counts.synced },
      { status: "failed", count: counts.failed },
      { status: "skipped", count: counts.skipped },
      { status: "processing", count: counts.processing },
    ],
    activity_chart: {
      labels,
      synced: syncedSeries,
      failed: failedSeries,
      skipped: skippedSeries,
    },
    amount_chart: {
      labels,
      amounts: amountSeries,
    },
  };
}
